import { useState, useEffect, useRef, useCallback } from 'react';
import { getCountries, getCountryCallingCode, Country } from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import { CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { InputAdornment } from '@mui/material';
import { RectangleRounded as RectangleRoundedIcon } from '@mui/icons-material';
import styled from 'styled-components';
import { SearchableSelect } from '../select';
import { Input, InputBaseProps } from './Input';
import { IconButton } from '../button';
import { maskPhoneNumber } from '../../utils';
import { countryCodes } from '../../const';

const allowedKeyPressKeyCodes = [48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 8, 37, 39];
const countryOptions = getCountries().map((country) => ({
  countryCode: country,
  phoneCode: getCountryCallingCode(country),
  flag: flags[country],
}));

const FlagIconButton = styled(IconButton)`
  margin-left: -0.8rem;
`;

interface FlagIconProps {
  countryCode?: Country;
  country?: keyof typeof countryCodes;
}

export const FlagIcon: React.FC<FlagIconProps> = ({ countryCode, country }: FlagIconProps) => {
  let FlagComponent: any = RectangleRoundedIcon;
  const props: any = {};

  if (country) {
    countryCode = countryCodes[country] as CountryCode;
  }

  if (countryCode) {
    FlagComponent = flags[countryCode];
    props.title = countryCode;
  }

  return <FlagComponent style={{ width: '2.4rem', height: '2.4rem' }} {...props} />;
};

export const FlagOptionWrapper = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  grid-column-gap: 1rem;
  align-items: center;
`;

interface InputPhoneNumberProps extends InputBaseProps {
  defaultCountry?: Country;
}

export const InputPhoneNumber: React.FC<InputPhoneNumberProps> = ({
  onChange,
  value,
  defaultValue = '+86',
  defaultCountry = 'CN',
  ...props
}: InputPhoneNumberProps) => {
  const inputRef = useRef<any>();
  const [countryCode, setCountryCode] = useState<Country | undefined>(defaultCountry);
  const selectedCountry = useRef<any>({});

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open) return;

    const isSelected = !!Object.keys(selectedCountry.current).length;
    if (!isSelected) return;

    setCountryCode(selectedCountry.current.countryCode);
    selectedCountry.current = {};
    inputRef.current.focus();
  }, [open]);

  const handleOnKeyPress = useCallback(
    (e: any) => {
      if (open) return;

      const keyCode = e.keyCode || e.which;
      if (!allowedKeyPressKeyCodes.includes(keyCode)) e.preventDefault();
    },
    [open],
  );

  const handleOnChange = (e: any) => {
    e.target.value = maskPhoneNumber(e.target.value);

    const parsed = parsePhoneNumberFromString(e.target.value);
    if (parsed?.country) setCountryCode(parsed?.country);

    onChange && onChange(e);
  };

  const handleFlagToggle = () => {
    setOpen((prev) => !prev);
  };

  const handleFlagSelect = (obj: any) => {
    const newValue: any = { target: { value: `+${obj.phoneCode}` } };

    selectedCountry.current = obj;
    onChange && onChange(newValue);
  };

  return (
    <Input
      ref={inputRef}
      value={maskPhoneNumber(typeof value === 'string' ? value : (defaultValue as string))}
      onChange={handleOnChange}
      onKeyPress={handleOnKeyPress}
      clickableIcon
      startAdornment={
        <InputAdornment position={'start'}>
          <SearchableSelect
            open={open}
            onToggle={handleFlagToggle}
            onSelect={handleFlagSelect}
            selectedAreasEqaul={{ key: 'countryCode', keys: [countryCode || ''] }}
            options={countryOptions.map((obj, i) => ({
              ...obj,
              id: 'flag:' + i,
              label: `${obj.countryCode} +${obj.phoneCode}`,
              Component: (
                <FlagOptionWrapper>
                  <FlagIcon countryCode={obj.countryCode} /> {obj.countryCode} +{obj.phoneCode}
                </FlagOptionWrapper>
              ),
            }))}
            HeaderComponent={
              <FlagIconButton onClick={handleFlagToggle}>
                <FlagIcon
                  countryCode={
                    parsePhoneNumberFromString((`+${value}` || defaultValue) as string)?.country ||
                    countryCode
                  }
                />
              </FlagIconButton>
            }
            MenuProps={
              inputRef.current
                ? {
                    anchorEl: inputRef.current,
                    PaperProps: {
                      style: {
                        width: inputRef.current.clientWidth,
                      },
                    },
                  }
                : {}
            }
          />
        </InputAdornment>
      }
      {...props}
    />
  );
};
